import type {
  AlternativeProducts,
  AlternativeProductsEntry,
  AlternativeQuotesDTO,
  QuoteDetailsDTO,
  QuoteEntryDTO,
  QuoteLineItemDTO,
} from "~/features/orders/quoteList/api/fetchOrderDetails/dto";
import type { QuoteDetailsMO, QuoteLineMO } from "~/features/orders/common/components/v2/models";
import { adjustFormat } from "~/utils/accountNumberUtils";
import { getItemStatus, type QuoteDetailsEntryStatus } from "~/domains/orders/types/GetItemStatus";
import * as PriceUtils from "~/utils/PriceUtils";
import * as WeightUtils from "~/utils/WeightUtils";
import { getWeightObject } from "~/utils/WeightUtils";
import { DEFAULT_UNIT_SYMBOL } from "~/utils/constants";
import type { OrderEntryDTO } from "~/features/orders/orderList/api/fetchOrderDetails/dto";
import type { OrderLineItemDTO } from "~/features/orders/orderList/api/fetchOrderHistory/dto";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import { isSubscription } from "~/utils/productUtils";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";

export function transformQuoteDetails(dto: QuoteDetailsDTO): QuoteDetailsMO {
  const accNo = adjustFormat(dto.accountNumber ?? "");

  return {
    accNo,
    orderStatus: dto.orderStatusCode,
    orderAttainment: dto.quoteResponsibleName,
    datePlaced: dto.datePlaced ? new Date(dto.datePlaced) : undefined,
    orderFulfilment: dto.fulfilmentResponsibleName,
    dateExpired: dto.dateExpired ? new Date(dto.dateExpired) : undefined,
    poNumber: dto.poNumber,
    soNumber: dto.soNumber,
    status: getItemStatus(dto.orderStatusCode),
    quoteType: dto.orderType,
    isBundledQuote: dto.isBundled,
    comment: dto.comment ?? undefined,
    quoteLines: (dto.entries ?? []).flatMap(transformToQuoteLineMO),
    totalPrice: dto.orderTotal,

    totalSelectablePrice: dto.selectableItemTotalPrice || PriceUtils.zero(),
    totalSelectableWeight: WeightUtils.getWeightObject(
      dto.selectableItemWeight || 0,
      dto.orderWeightUnit !== null ? dto.orderWeightUnit : DEFAULT_UNIT_SYMBOL,
    ),
    quoteDocumentUrl: dto.quoteDocumentUrl,

    disabled: !dto.checkoutAllowed,
  };
}

function transformToQuoteLineMO(dto: QuoteEntryDTO): QuoteLineMO[] {
  const alternativeQuoteLines = dto.alternativeQuotes ?? [];
  const alternativeProductQuoteLines = dto.alternativeProducts ?? [];

  return [
    transformMainQuoteLine(dto),
    ...alternativeQuoteLines.map((alternativeQuoteLine) => transformAlternativeQuoteLines(dto, alternativeQuoteLine)),
    ...alternativeProductQuoteLines.flatMap((alternativeProductGroup) =>
      alternativeProductGroup.entries.map((alternativeProductQuoteLine) =>
        transformAlternativeProductQuoteLines(dto, alternativeProductGroup, alternativeProductQuoteLine),
      ),
    ),
  ];
}

function getNetPrice(
  data: OrderEntryDTO | QuoteLineItemDTO | OrderLineItemDTO,
  priceUnit: string,
  product: ProductData,
): NetPriceData {
  const surcharge = data.surcharge ?? 0;
  const baseValue = data.nettoPrice.value;
  return {
    value: baseValue * (1 + surcharge),
    baseValue,
    currencyIso: data.nettoPrice.currencyIso,
    unit: +priceUnit || 1,
    surcharge,
    isSubscription: isSubscription(product),
    rebate: undefined,
    isSpecial: undefined,
  };
}

function transformMainQuoteLine(dto: QuoteEntryDTO): QuoteLineMO {
  const mainQuoteParts = dto.mainQuotes ?? [];
  const firstMainQuotePart = mainQuoteParts[0];

  const status = getItemStatus(firstMainQuotePart.lineItemStatusCode);
  const messages: string[] = [];
  updateMessagesFromStatus(messages, status);
  const product = transformProduct(dto.product);
  return {
    lineNo: firstMainQuotePart.lineNumber.toString(),
    packCode: dto.packCode,
    packSize: firstMainQuotePart.packSize,
    priceReference: dto.priceReference ?? "",
    priceReferenceDescription: dto.priceReferenceDescription ?? "",
    product,
    warehouseName: dto.primaryWarehouseName,
    status,
    messages,
    weight: getWeightObject(dto.massPerPiece, dto.massUnit),
    parts: mainQuoteParts.map((part) => ({
      lineNo: part.scheduleLineNumber.toString(),
      confirmedQuantity: part.quantityConfirmed,
      requestedQuantity: part.quantityRequested,
      estimatedDeliveryDate: part.estimatedShippingDate ? new Date(part.estimatedShippingDate) : undefined,
      requestedDeliveryDate: part.requestedDeliveryDate ? new Date(part.requestedDeliveryDate) : undefined,
      price: getNetPrice(part, dto.priceUnit, product),
      logisticalComment: part.logisticalComment,
    })),
    disabled: calculateIsQuoteLineDisabled(dto.orderable, status, product),
    comment: firstMainQuotePart.comment,
  };
}

function transformAlternativeQuoteLines(
  mainQuoteLine: QuoteEntryDTO,
  alternativeQuoteLine: AlternativeQuotesDTO,
): QuoteLineMO {
  const mainQuoteParts = mainQuoteLine.mainQuotes ?? [];
  const firstMainQuotePart = mainQuoteParts[0];

  const alternativeQuoteParts = alternativeQuoteLine.entries ?? [];
  const firstAlternativeQuotePart = alternativeQuoteParts[0];

  const status = getItemStatus(firstAlternativeQuotePart.lineItemStatusCode);
  const messages: string[] = [];
  updateMessagesFromStatus(messages, status);
  const product = transformProduct(mainQuoteLine.product);
  return {
    lineNo: alternativeQuoteLine.index.toString(),
    alternativeTo: firstMainQuotePart.lineNumber.toString(),
    packCode: mainQuoteLine.packCode,
    packSize: firstMainQuotePart.packSize,
    priceReference: mainQuoteLine.priceReference ?? "",
    priceReferenceDescription: mainQuoteLine.priceReferenceDescription ?? "",
    product,
    warehouseName: mainQuoteLine.primaryWarehouseName,
    status,
    messages,
    weight: getWeightObject(mainQuoteLine.massPerPiece, mainQuoteLine.massUnit),
    parts: alternativeQuoteParts.map((part) => ({
      lineNo: part.scheduleLineNumber.toString(),
      confirmedQuantity: part.quantityConfirmed,
      requestedQuantity: part.quantityRequested,
      estimatedDeliveryDate: part.estimatedShippingDate ? new Date(part.estimatedShippingDate) : undefined,
      requestedDeliveryDate: part.requestedDeliveryDate ? new Date(part.requestedDeliveryDate) : undefined,
      price: getNetPrice(part, mainQuoteLine.priceUnit, product),
      logisticalComment: part.logisticalComment,
    })),
    disabled: calculateIsQuoteLineDisabled(mainQuoteLine.orderable, status, product),
    comment: firstAlternativeQuotePart.comment,
  };
}

function transformAlternativeProductQuoteLines(
  mainQuoteLine: QuoteEntryDTO,
  alternativeProductGroup: AlternativeProducts,
  alternativeProductQuoteLine: AlternativeProductsEntry,
): QuoteLineMO {
  const mainQuoteParts = mainQuoteLine.mainQuotes ?? [];
  const firstMainQuotePart = mainQuoteParts[0];

  const alternativeProductQuoteLineParts = alternativeProductQuoteLine.entries ?? [];
  const firstAlternativeProductQuotePart = alternativeProductQuoteLineParts[0];

  const status = getItemStatus(firstAlternativeProductQuotePart.lineItemStatusCode);
  const messages: string[] = [];
  updateMessagesFromStatus(messages, status);
  const product = transformProduct(alternativeProductGroup.product);
  return {
    lineNo: alternativeProductQuoteLine.index.toString(),
    alternativeTo: firstMainQuotePart.lineNumber.toString(),
    packCode: alternativeProductGroup.packCode,
    packSize: firstAlternativeProductQuotePart.packSize,
    priceReference: alternativeProductGroup.priceReference ?? "",
    priceReferenceDescription: alternativeProductGroup.priceReferenceDescription ?? "",
    product,
    warehouseName: alternativeProductGroup.primaryWarehouseName,
    status,
    messages,
    weight: getWeightObject(alternativeProductGroup.massPerPiece, alternativeProductGroup.massUnit),
    parts: alternativeProductQuoteLineParts.map((part) => ({
      lineNo: part.scheduleLineNumber!.toString(),
      confirmedQuantity: part.quantityConfirmed,
      requestedQuantity: part.quantityRequested,
      estimatedDeliveryDate: part.estimatedShippingDate ? new Date(part.estimatedShippingDate) : undefined,
      requestedDeliveryDate: part.requestedDeliveryDate ? new Date(part.requestedDeliveryDate) : undefined,
      price: getNetPrice(part, alternativeProductGroup.priceUnit, product),
      logisticalComment: part.logisticalComment,
    })),
    disabled: calculateIsQuoteLineDisabled(alternativeProductGroup.orderable, status, product),
    comment: firstAlternativeProductQuotePart.comment,
  };
}

function calculateIsQuoteLineDisabled(
  orderable: boolean,
  status: QuoteDetailsEntryStatus,
  product: ProductData,
): boolean {
  return !orderable || !product.url || isProductPMA(fakeDescriptorFromProduct(product));
}

function updateMessagesFromStatus(messages: string[], status: QuoteDetailsEntryStatus) {
  // Messages removed based on discussion with Willy in eTransact refinement 04.03.2024
  // if (status === QuoteDetailsEntryStatus.IN_CLARIFICATION) {
  //   messages.push("page.quoteDetails.message.notAvailable2");
  // } else if (status === QuoteDetailsEntryStatus.EXPIRED || status === QuoteDetailsEntryStatus.REJECTED) {
  //   messages.push("page.quoteDetails.message.notAvailable");
  // }
}
